import type {
  AvailableWorkflowDTO,
  ClinicWorkflowArtifact,
  ClinicWorkflowRun,
  WorkflowMeta,
  WorkflowRunOutput,
  WorkflowRunProgress,
  WorkflowRunStatus,
} from "../../../hexagon/core/ports/driven/neoWorkflowRepository";
import { parseServerDate } from "../../utils/serverDate";

export type HomeWorkflowActivityKind =
  | "running"
  | "ready"
  | "scheduled"
  | "result"
  | "failed"
  | "paused";

export interface HomeWorkflowActivity {
  id: string;
  workflowId?: number;
  workflowIdStr?: string;
  projectId?: number | null;
  name: string;
  kind: HomeWorkflowActivityKind;
  scheduledAt?: string | null;
  run?: WorkflowRunProgress | ClinicWorkflowRun;
  output?: WorkflowRunOutput | ClinicWorkflowArtifact;
}

const NON_TERMINAL_RUN_STATUSES: ReadonlySet<WorkflowRunStatus> = new Set([
  "running",
  "awaiting_input",
  "awaiting_approval",
  "awaiting_delivery",
  "delivering",
]);

// A run that finished and delivered. Neo runs end ``done``; clinic
// (catalog) runs end ``sent``, and later ``purged`` once retention removes
// their files — still a run that happened, just without downloads.
const RESULT_RUN_STATUSES = new Set([
  "done",
  "completed",
  "generated",
  "sent",
  "delivered",
  "purged",
]);

const normalizedName = (value: string): string =>
  value.trim().toLocaleLowerCase();

export const isMondayBriefingName = (value: string): boolean => {
  const name = normalizedName(value);
  return (
    (name.includes("monday") &&
      (name.includes("briefing") || name.includes("brief"))) ||
    // Its user-facing name, in case the catalog adopts it.
    name.includes("weekly clinical briefing")
  );
};

const numericWorkflowId = (value: unknown): number | undefined => {
  if (typeof value === "number" && Number.isSafeInteger(value) && value > 0) {
    return value;
  }
  if (typeof value !== "string" || !/^\d+$/.test(value.trim())) {
    return undefined;
  }
  const parsed = Number(value);
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : undefined;
};

export const workflowIdForAvailable = (
  available: AvailableWorkflowDTO,
  workflows: WorkflowMeta[]
): number | undefined => {
  const metadata = available.extraInfo ?? {};
  const explicitId = [
    available.id,
    metadata.workflow_id,
    metadata.workflowId,
    metadata.neo_workflow_id,
  ]
    .map(numericWorkflowId)
    .find((id) => id !== undefined);
  if (explicitId !== undefined) return explicitId;

  const name = normalizedName(available.name);
  return workflows.find((workflow) => normalizedName(workflow.name) === name)
    ?.id;
};

interface WorkflowSource {
  key: string;
  name: string;
  workflowId?: number;
  meta?: WorkflowMeta;
  subscribed: boolean;
  status?: string;
  nextRunAt?: string | null;
}

type HomeRun = WorkflowRunProgress | ClinicWorkflowRun;

const runArtifacts = (
  run: HomeRun
): Array<WorkflowRunOutput | ClinicWorkflowArtifact> =>
  "artifacts" in run ? run.artifacts : run.outputs;

/** When a run finished: sent for clinic runs, else completed, else started. */
const runTime = (run: HomeRun): number => {
  const value =
    ("artifacts" in run ? run.sent_at : undefined) ??
    run.completed_at ??
    run.started_at;
  const time = value ? parseServerDate(value).getTime() : Number.NaN;
  return Number.isNaN(time) ? 0 : time;
};

const sourceActivities = (
  source: WorkflowSource,
  runs: HomeRun[],
  completedSince?: number
): HomeWorkflowActivity[] => {
  const running = runs.find((run) =>
    NON_TERMINAL_RUN_STATUSES.has(run.status as WorkflowRunStatus)
  );
  // A catalog workflow whose latest report is built but waits for its send
  // window. Shown as one "ready" card, not as generating or as a result.
  const ready = !running && source.status === "ready_to_send";
  // The newest finished run, if it finished recently enough to list.
  const result = runs
    .filter(
      (run) =>
        RESULT_RUN_STATUSES.has(run.status.toLowerCase()) &&
        !(ready && run.status.toLowerCase() === "generated")
    )
    .sort((left, right) => runTime(right) - runTime(left))
    .find(
      (run) => completedSince === undefined || runTime(run) >= completedSince
    );
  const activities: HomeWorkflowActivity[] = [];
  const workflowIdStr = source.key.startsWith("catalog:")
    ? source.key.slice("catalog:".length)
    : undefined;

  if (running || source.status === "running") {
    activities.push({
      id: `${source.key}:running:${running?.run_id ?? "current"}`,
      workflowId: source.workflowId,
      workflowIdStr,
      projectId: source.meta?.project_id,
      name: source.name,
      kind: "running",
      run: running,
    });
  }

  if (ready) {
    activities.push({
      id: `${source.key}:ready`,
      workflowId: source.workflowId,
      workflowIdStr,
      projectId: source.meta?.project_id,
      name: source.name,
      kind: "ready",
    });
  } else if (source.status === "failed" || source.status === "paused") {
    activities.push({
      id: `${source.key}:${source.status}`,
      workflowId: source.workflowId,
      workflowIdStr,
      projectId: source.meta?.project_id,
      name: source.name,
      kind: source.status,
    });
  } else if (
    source.status !== "running" &&
    (source.status === "scheduled" ||
      source.subscribed ||
      source.nextRunAt ||
      source.meta?.next_run_at)
  ) {
    activities.push({
      id: `${source.key}:scheduled`,
      workflowId: source.workflowId,
      workflowIdStr,
      projectId: source.meta?.project_id,
      name: source.name,
      kind: "scheduled",
      scheduledAt: source.nextRunAt ?? source.meta?.next_run_at,
    });
  }

  if (result) {
    const artifacts = runArtifacts(result);
    activities.push({
      id: `${source.key}:result:${result.run_id}`,
      workflowId: source.workflowId,
      workflowIdStr,
      projectId: source.meta?.project_id,
      name: source.name,
      kind: "result",
      run: result,
      output: artifacts[0],
    });
  }

  return activities;
};

export const activityTime = (activity: HomeWorkflowActivity): number => {
  const timestamp =
    activity.kind === "scheduled"
      ? activity.scheduledAt
      : (activity.run && "artifacts" in activity.run
          ? activity.run.sent_at
          : undefined) ??
        activity.run?.completed_at ??
        activity.run?.started_at;
  const value = timestamp ? parseServerDate(timestamp).getTime() : Number.NaN;
  return Number.isNaN(value) ? 0 : value;
};

const KIND_ORDER: Record<HomeWorkflowActivityKind, number> = {
  running: 0,
  ready: 1,
  scheduled: 2,
  failed: 3,
  paused: 4,
  result: 5,
};

export const buildHomeWorkflowActivities = ({
  workflows,
  availableWorkflows,
  runsByWorkflowId,
  clinicRunsByWorkflowId,
  completedSince,
}: {
  workflows: WorkflowMeta[];
  availableWorkflows: AvailableWorkflowDTO[];
  runsByWorkflowId: Record<number, WorkflowRunProgress[]>;
  clinicRunsByWorkflowId?: Record<string, ClinicWorkflowRun[]>;
  /** Only list a workflow as completed if its run finished at or after this
   *  time (epoch ms). */
  completedSince?: number;
}): HomeWorkflowActivity[] => {
  const workflowById = new Map(
    workflows.map((workflow) => [workflow.id, workflow])
  );
  const catalogWorkflowIds = new Set<number>();
  const sources: WorkflowSource[] = [];

  availableWorkflows.forEach((available) => {
    const workflowId = workflowIdForAvailable(available, workflows);
    if (workflowId !== undefined) catalogWorkflowIds.add(workflowId);
    if (!available.enabled && !available.status) return;
    const meta =
      workflowId !== undefined ? workflowById.get(workflowId) : undefined;
    sources.push({
      key: `catalog:${available.id}`,
      name: available.name,
      workflowId,
      meta,
      subscribed: true,
      status: available.status,
      nextRunAt: available.nextRunAt,
    });
  });

  workflows.forEach((workflow) => {
    if (catalogWorkflowIds.has(workflow.id)) return;
    sources.push({
      key: `workflow:${workflow.id}`,
      name: workflow.name,
      workflowId: workflow.id,
      meta: workflow,
      subscribed: false,
    });
  });

  return sources
    .flatMap((source) =>
      sourceActivities(
        source,
        source.key.startsWith("catalog:")
          ? clinicRunsByWorkflowId !== undefined
            ? clinicRunsByWorkflowId[source.key.slice("catalog:".length)] ?? []
            : source.workflowId !== undefined
            ? runsByWorkflowId[source.workflowId] ?? []
            : []
          : source.workflowId !== undefined
          ? runsByWorkflowId[source.workflowId] ?? []
          : [],
        completedSince
      )
    )
    .sort((left, right) => {
      const byKind = KIND_ORDER[left.kind] - KIND_ORDER[right.kind];
      if (byKind !== 0) return byKind;
      const leftTime = activityTime(left);
      const rightTime = activityTime(right);
      return left.kind === "scheduled"
        ? leftTime - rightTime
        : rightTime - leftTime;
    });
};

export const isHomeWorkflowRunActive = (status: WorkflowRunStatus): boolean =>
  NON_TERMINAL_RUN_STATUSES.has(status);
